// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
} from 'common.data';
import type {
  $DateTimeFormats,
} from 'common.helpers';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getFileUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  getDateTimeFormats,
} from 'common.helpers/dist/browser';
import {
  regattaMessageTypeLabel,
} from 'common.language';
import {
  richText,
} from '../../../../helpers';
import config from '../../../../config';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  item: $Model<'Regatta'>;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'RegattaMessage'>>;
  loading: boolean;
};

class MessagesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.setupSocket();
    this.getData();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getRegattaMessagesSectionData({
        RegattaId: item.id,
      });

      data.reverse();

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.RegattaId === item.id && data.modelName === 'RegattaMessage') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  renderItem(dataItem: $Model<'RegattaMessage'>, index: number) {
    const {
      main,
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    const createdAtMoment = moment(dataItem.createdAt);

    const formats: $DateTimeFormats = getDateTimeFormats({
      RegionId: main.RegionId,
      regions: main.regions,
    });

    const dateTimeFormat = `${formats.Date} ${formats.Time}`;
    const messageDateTime: string = createdAtMoment.format(dateTimeFormat);

    const typeLabel: string = regattaMessageTypeLabel[dataItem.Type];
    const typeLabelShort: string = typeLabel.split(' ').map((word: string) => word[0]).join('');

    return (
      <React.Fragment key={dataItem.id}>
        <div
          className={'border-t flex flex-row w-full sm:items-center px-1'}
          data-target={`#accordion-${dataItem.id}`}
          data-toggle={'collapse'}
        >
          <h5 className={`py-5 ${index === 0 ? 'active' : ''} grow mr-2`}>
            <span className={'uppercase font-bold grow mr-3'}>
              {`#${data.length - index} ${dataItem.Name}`}
            </span>
          </h5>
          <div>
            <div className={'flex flex-row items-end py-5 mb-auto'}>
              <div
                className={'badge badge_outlined badge_secondary uppercase px-1 py-0.5 mr-2'}
                title={t(typeLabel)}
              >
                {typeLabelShort}
              </div>
              <div className={'flex flex-row items-center'}>
                <span
                  className={'ml-auto text-secondary whitespace-nowrap mr-2'}
                  title={messageDateTime}
                >
                  {createdAtMoment.fromNow()}
                </span>
                <span className={'collapse-indicator la la-arrow-circle-down'} />
              </div>
            </div>
          </div>
        </div>
        <div
          className={`collapse ${index === 0 ? 'open' : ''} px-1`}
          id={`accordion-${dataItem.id}`}
        >
          <div className={'pb-5 pt-0'}>
            <div className={'flex flex-row mb-2'}>
              <div className={'grow rich-text break-words'}>
                {richText(dataItem.Content)}
              </div>
              {dataItem.File && (
                <div>
                  <a
                    className={'btn btn_primary btn_outlined uppercase whitespace-nowrap py-1 mt-auto'}
                    href={getFileUrl({
                      cdnUrl: config.CDN_URL,
                      file: dataItem.File,
                    })}
                    rel={'noreferrer'}
                    target={'_blank'}
                    title={t('Attachment')}
                  >
                    <em className={'la la-file text-2xl'} />
                    <span className={'ml-2 hidden sm:block'}>
                      {t('Attachment')}
                    </span>
                  </a>
                </div>
              )}
            </div>
            <small className={'text-gray-600'}>
              {`${messageDateTime}  ${t('by')} ${typeLabel}`}
            </small>
          </div>
        </div>
      </React.Fragment>
    );
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    const shorten: boolean = data.length > 5;

    return (data.length > 0 ? (
      <Section
        id={webSections[webPages.regattaPage].messages}
        shorten={shorten}
        shortenButtonLabel={t('Show all messages')}
        title={t('Messages')}
      >
        <Loader
          fade
          loading={loading === true}
        >
          <div className={`accordion border border-gray-300 border-0 bg-white rounded-lg ${shorten === true ? 'px-5' : ''}`}>
            {data.map((dataItem: $Model<'RegattaMessage'>, index: number) => this.renderItem(
              dataItem,
              index,
            ))}
          </div>
        </Loader>
      </Section>
    ) : null);
  }
}

export default MessagesSectionView;
